/**
 * Pure helpers for "Hilos con seguimiento" (thread tracking) — the overlay
 * that turns an existing team-chat thread (parent message + replies) into a
 * trackable work item: estado, responsable, fecha límite, prioridad.
 *
 * Spec: docs/superpowers/specs/2026-07-07-thread-tracking-design.md (enfoque A:
 * el hilo sigue siendo un mensaje; el tracking es una tabla 1:1 encima).
 *
 * // contrato propuesto #93497 — los shapes vienen de NUESTRA propuesta al
 * backend (Matrix #93497) y él puede desviar: por eso TODO campo es opcional
 * y toda función tolera datos parciales/ausentes/ inválidos sin lanzar.
 *
 * No React here — plain .ts with a .test.ts sibling (same pattern as
 * message-order.ts / scroll-stick.ts).
 */

import { translate } from '@/lib/i18n/runtime';
import { trackedDueCopy, trackedStatusCopy } from './page-copy';

export type ThreadTrackingStatus = 'open' | 'in_progress' | 'resolved';
export type ThreadTrackingPriority = 'low' | 'normal' | 'high';

/**
 * The tracking block — embedded on the PARENT message DTO (`tracking?`) and
 * returned whole by PUT /messages/:id/tracking. Everything optional: the
 * backend confirmará el contrato y puede omitir/añadir campos.
 */
export interface ThreadTracking {
  parent_message_id?: string;
  channel_id?: string;
  title?: string;
  /** 'open' | 'in_progress' | 'resolved' — string-widened for tolerance. */
  status?: ThreadTrackingStatus | string;
  /** 'low' | 'normal' | 'high' — string-widened for tolerance. */
  priority?: ThreadTrackingPriority | string;
  assignee_id?: string | null;
  assignee_name?: string;
  due_at?: string | null;
  created_by?: string;
  resolved_at?: string | null;
  created_at?: string;
  updated_at?: string;
  reply_count?: number;
}

/**
 * One row of a tracked-threads list (tab "Hilos" del canal, bandeja global
 * "Seguimientos"): the tracking block + a snippet of the parent message +
 * the reply count. `channel_name` only makes sense on the cross-channel
 * bandeja. // contrato propuesto #93497
 */
export interface TrackedThreadRow {
  tracking?: ThreadTracking;
  /** Snippet del mensaje padre — con lo justo para pintar la fila. */
  parent?: { content?: string; user_name?: string };
  reply_count?: number;
  channel_name?: string;
  channel_id?: string;
  parent_message_id?: string;
}

/** String coercion for tolerant DTO fields — non-strings read as absent. */
function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined;
}

/**
 * Normalize one raw row from the threads/tracked-threads endpoints into a
 * `TrackedThreadRow`. Contrato CONFIRMADO #93544 — el backend devuelve:
 *
 * - `GET /me/tracked-threads` → `{ channel_id, channel_name, message: MessageDTO, tracking }`
 * - `GET /channels/:id/threads` → el MessageDTO PADRE con `tracking` embebido
 *   (reply_count ya vive en el MessageDTO — desviación 2 de #93544).
 *
 * Nuestra propuesta original (`{tracking, parent, …}`) se acepta como
 * passthrough por si algún shape intermedio la usa. Garbage → null.
 */
export function normalizeTrackedRow(raw: unknown): TrackedThreadRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const tracking = (r.tracking && typeof r.tracking === 'object' ? r.tracking : undefined) as
    | ThreadTracking
    | undefined;

  // Shape bandeja (#93544): { channel_id, channel_name, message, tracking }.
  const msg = (r.message && typeof r.message === 'object' ? r.message : undefined) as
    | Record<string, unknown>
    | undefined;
  if (msg) {
    const msgTracking = (msg.tracking && typeof msg.tracking === 'object'
      ? msg.tracking
      : undefined) as ThreadTracking | undefined;
    const t = tracking ?? msgTracking;
    return {
      tracking: t,
      parent: {
        content: str(msg.content),
        user_name: str(msg.user_name) ?? str(msg.display_name),
      },
      reply_count:
        typeof msg.reply_count === 'number'
          ? msg.reply_count
          : typeof r.reply_count === 'number'
            ? r.reply_count
            : undefined,
      channel_id: str(r.channel_id) ?? str(msg.channel_id) ?? str(t?.channel_id),
      channel_name: str(r.channel_name),
      parent_message_id: str(msg.id) ?? str(t?.parent_message_id),
    };
  }

  // Shape propuesta nuestra ({tracking, parent, …}) — passthrough con
  // fallbacks desde el tracking embebido.
  if (r.parent && typeof r.parent === 'object') {
    const row = r as TrackedThreadRow;
    return {
      ...row,
      channel_id: str(row.channel_id) ?? str(tracking?.channel_id),
      parent_message_id: str(row.parent_message_id) ?? str(tracking?.parent_message_id),
    };
  }

  // Shape tab del canal (#93544): el row ES el MessageDTO padre con tracking.
  if ('id' in r || 'content' in r) {
    return {
      tracking,
      parent: {
        content: str(r.content),
        user_name: str(r.user_name) ?? str(r.display_name),
      },
      reply_count: typeof r.reply_count === 'number' ? r.reply_count : undefined,
      channel_id: str(r.channel_id) ?? str(tracking?.channel_id),
      channel_name: str(r.channel_name),
      parent_message_id: str(r.id) ?? str(tracking?.parent_message_id),
    };
  }

  // Solo tracking (sin padre) — fila mínima; los consumidores toleran
  // parent ausente (fallback 'Hilo sin título').
  if (tracking) {
    return {
      tracking,
      channel_id: str(r.channel_id) ?? str(tracking.channel_id),
      parent_message_id: str(tracking.parent_message_id),
    };
  }
  return null;
}

export interface DueInfo {
  /** Human label in the current personal language. */
  label: string;
  /** 'overdue' = ya venció; 'soon' = vence en <24h; 'ok' = hay margen. */
  tone: 'ok' | 'soon' | 'overdue';
}

/**
 * Relative due-date label + urgency tone.
 *
 * - `null` when there's no date or it's unparseable (row renders no chip).
 * Product grammar/date use the personal locale, sharing the page formatter.
 * Urgency keeps the existing elapsed-time buckets: overdue, <24h and later.
 * The existing 24–48h "tomorrow" label is not a civil-calendar calculation.
 */
export function formatDue(
  dueAt: string | null | undefined,
  now: Date | number = Date.now(),
): DueInfo | null {
  const nowMs = typeof now === 'number' ? now : now.getTime();
  if (!Number.isFinite(nowMs)) return null;
  return trackedDueCopy(dueAt, nowMs);
}

const PRIORITY_RANK: Record<string, number> = { high: 0, normal: 1, low: 2 };

/** Epoch ms of a row's due date; NaN when absent/unparseable. */
function dueMs(row: TrackedThreadRow): number {
  const iso = row.tracking?.due_at;
  return iso ? Date.parse(iso) : Number.NaN;
}

/** Epoch ms of a row's updated_at; NaN when absent/unparseable. */
function updatedMs(row: TrackedThreadRow): number {
  const iso = row.tracking?.updated_at;
  return iso ? Date.parse(iso) : Number.NaN;
}

/**
 * Bandeja ordering: overdue first, then due_at asc (rows without a due date
 * after dated ones), then priority (high > normal > low; unknown/missing
 * reads as 'normal'), then updated_at desc (freshest activity first; rows
 * without updated_at last). Returns a new array; never mutates the input.
 */
export function sortTrackedThreads(
  rows: TrackedThreadRow[] | undefined,
  now: Date | number = Date.now(),
): TrackedThreadRow[] {
  if (!rows || rows.length === 0) return [];
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const isOverdue = (r: TrackedThreadRow) => {
    const t = dueMs(r);
    return !Number.isNaN(t) && t < nowMs;
  };
  return [...rows].sort((a, b) => {
    // 1. Overdue first.
    const oa = isOverdue(a);
    const ob = isOverdue(b);
    if (oa !== ob) return oa ? -1 : 1;
    // 2. due_at ascending; undated rows after dated ones.
    const da = dueMs(a);
    const db = dueMs(b);
    const ma = Number.isNaN(da);
    const mb = Number.isNaN(db);
    if (ma !== mb) return ma ? 1 : -1;
    if (!ma && da !== db) return da - db;
    // 3. Priority: high > normal > low (unknown → normal).
    const pa = PRIORITY_RANK[a.tracking?.priority ?? 'normal'] ?? 1;
    const pb = PRIORITY_RANK[b.tracking?.priority ?? 'normal'] ?? 1;
    if (pa !== pb) return pa - pb;
    // 4. updated_at descending; missing → end.
    const ua = updatedMs(a);
    const ub = updatedMs(b);
    const uma = Number.isNaN(ua);
    const umb = Number.isNaN(ub);
    if (uma !== umb) return uma ? 1 : -1;
    if (!uma && ua !== ub) return ub - ua;
    return 0;
  });
}

/** Personal-language label; unknown machine values remain literal. */
export function statusLabel(status?: string | null): string {
  return trackedStatusCopy(status);
}

/** Personal-language label; unknown machine values remain literal. */
export function priorityLabel(priority?: string | null): string {
  const value = priority ?? 'normal';
  return ['low', 'normal', 'high'].includes(value)
    ? translate('priority.' + value, {}, 'teamchat') : value;
}

/**
 * "Open" for tab/badge purposes = anything not resolved. A missing status
 * reads as the backend default 'open' → true. Unknown future statuses also
 * count as open (safer to keep them visible in pendientes than to hide them).
 */
export function isOpenStatus(status?: string | null): boolean {
  return status !== 'resolved';
}

/** Shallow key/value equality — enough for the flat ThreadTracking record. */
function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k]);
}

/**
 * Merge a tracking delta into a (parent) message — the transform used by the
 * surgical cache patch (`patchChannelMessagesCache`) on upsert/remove and on
 * the `thread:tracking_updated` WS event.
 *
 * - `tracking: null` → REMOVES the block (DELETE /tracking). No-op reference
 *   when the message had none.
 * - `tracking: undefined` → nothing to merge; returns the SAME reference.
 * - object → shallow-merged over the existing block (a partial upsert
 *   response keeps fields it didn't touch). Returns the SAME reference when
 *   the merge changes nothing, so React Query observers don't re-render on
 *   duplicate/stale events.
 */
export function mergeTrackingIntoMessage<T extends { tracking?: ThreadTracking | null }>(
  msg: T,
  tracking: Partial<ThreadTracking> | null | undefined,
): T {
  if (tracking === null) {
    if (msg.tracking == null) return msg;
    return { ...msg, tracking: undefined };
  }
  if (!tracking) return msg;
  const prev = msg.tracking ?? undefined;
  const next: ThreadTracking = { ...(prev ?? {}), ...tracking };
  if (prev && shallowEqual(prev as Record<string, unknown>, next as Record<string, unknown>)) {
    return msg;
  }
  return { ...msg, tracking: next };
}
